<template>
    <div class="details">
        <h2 class="title">
            Site Details
        </h2>
        <Basics :site-details="siteDetails" />
        <div class="more-details">
            <h2 class="title">
                More Details

                <a
                    v-if="isAllExpandable"
                    href="#expand-all"
                    @click="toggleAllSections"
                >Expand All</a>
                <a
                    v-else
                    href="#retract-all"
                    @click="toggleAllSections"
                >Collapse All</a>
            </h2>
        </div>
        <Section
            section="site-information"
            title="Site Information"
            :toggle-section="toggleSection"
            :is-expanded="isExpanded"
        >
            <SiteInformation :site-details="siteDetails" />
        </Section>
        <Section
            section="contamination-details"
            title="Contamination Details"
            :toggle-section="toggleSection"
            :is-expanded="isExpanded"
            :is-expandable="siteDetails.contaminants.length > 0"
        >
            <ContaminationDetails :site-details="siteDetails" />
        </Section>
        <Section
            section="remediation-system"
            title="Remediation System"
            :toggle-section="toggleSection"
            :is-expanded="isExpanded"
        >
            <RemediationSystem :site-details="siteDetails" />
        </Section>
        <Section
            section="risk-management"
            title="Risk Management"
            :toggle-section="toggleSection"
            :is-expanded="isExpanded"
        >
            <RiskManagement :site-details="siteDetails" />
        </Section>
        <Section
            section="groundwater-&-soil-grain-size"
            title="Groundwater & Soil Grain Size"
            :toggle-section="toggleSection"
            :is-expanded="isExpanded"
        >
            <GroundwaterAndSoilGrainSize :site-details="siteDetails" />
        </Section>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import _ from 'lodash';

import ESARSite from '@/models/ESARSite';

import Section from './Section.vue';
import Basics from './Basics.vue';
import SiteInformation from './SiteInformation.vue';
import ContaminationDetails from './ContaminationDetails.vue';
import RemediationSystem from './RemediationSystem.vue';
import RiskManagement from './RiskManagement.vue';
import GroundwaterAndSoilGrainSize from './GroundwaterAndSoilGrainSize.vue';

type SectionName = 'site-information' | 'contamination-details' | 'remediation-system' | 'risk-management' | 'groundwater-&-soil-grain-size';
type Sections = {
    'site-information': boolean,
    'contamination-details': boolean,
    'remediation-system': boolean,
    'risk-management': boolean,
    'groundwater-&-soil-grain-size': boolean,
}

export default defineComponent({
    name: 'Details',
    components: {
        Section,
        Basics,
        SiteInformation,
        ContaminationDetails,
        RemediationSystem,
        RiskManagement,
        GroundwaterAndSoilGrainSize
    },
    props: {
        siteDetails: ESARSite
    },
    setup(props) {
        const sections = {
            'site-information': false,
            'contamination-details': false,
            'remediation-system': false,
            'risk-management': false,
            'groundwater-&-soil-grain-size': false,
        };

        const sectionsExpanded = ref<Sections>(sections);

        const isAllExpandable = ref<boolean>(true);

        const isExpanded = (s: SectionName) => sectionsExpanded.value[s];

        const setIsAllExpandable = () => {
            isAllExpandable.value = _.values(sectionsExpanded.value).some((i) => i === false);
        };

        const toggleSection = (s: SectionName) => {
            sectionsExpanded.value[s] = !sectionsExpanded.value[s];
            setIsAllExpandable();
        };

        const toggleAllSections = (e: Event) => {
            e.preventDefault();
            _.keys(sectionsExpanded.value).forEach((k) => {
                sectionsExpanded.value[k as SectionName] = isAllExpandable.value;
            });
            setIsAllExpandable();
            return false;
        };

        return {
            isAllExpandable,
            isExpanded,
            toggleSection,
            toggleAllSections,
        };
    }
});
</script>

<style lang="postcss" scoped>
.details {
    /** Sections */
    .more-details {
        border-top: 1px solid #CCC;

        a {
            @apply text-header-link text-sm font-normal italic;
        }

        .title {
            @apply flex items-baseline justify-between mt-2 mb-2;
        }
    }

    .section:last-child {
        border-bottom: 1px solid #CCC;
        @apply mb-3;
    }

    /** Elements */
    .title {
        @apply text-lg font-bold;
    }

    .subtitle {
        @apply text-sm font-bold;
    }
}
</style>
